/**
 * Usernames.
 *
 * Staff sign in with one, and they are globally unique — so the server can find
 * a person from the credential alone, with no pharmacy code typed on the form.
 *
 * The cost of global uniqueness is that the first shop to claim "maryan" owns
 * it. `suggestUsernames` is the mitigation: an admin adding staff is offered
 * names that are free rather than left to guess, so the friction never reaches
 * them.
 */

/** Lowercase letters, digits, dot and underscore. Three to thirty characters. */
const VALID = /^[a-z][a-z0-9._]{2,29}$/;

export class UsernameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UsernameError";
  }
}

/**
 * Canonical form. Uniqueness is enforced case-insensitively in the database, so
 * comparison and storage must agree on one casing — this is it.
 */
export function normalizeUsername(input: string): string {
  return input.trim().toLowerCase();
}

export function isValidUsername(input: string): boolean {
  return VALID.test(normalizeUsername(input));
}

/**
 * Why a username was rejected, in words a person can act on.
 *
 * "Invalid username" tells somebody nothing; it makes them try variations until
 * one sticks. Naming the rule they broke lets them fix it once.
 */
export function explainUsername(input: string): string | null {
  const u = normalizeUsername(input);
  if (u.length < 3) return "Too short — at least 3 characters.";
  if (u.length > 30) return "Too long — at most 30 characters.";
  if (!/^[a-z]/.test(u)) return "Must start with a letter.";
  if (!/^[a-z0-9._]+$/.test(u)) return "Only letters, numbers, dots and underscores.";
  return VALID.test(u) ? null : "Not a valid username.";
}

/** Strip accents and anything not allowed, so a person's real name can seed one. */
function slug(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .replace(/^[^a-z]+/, "");
}

/**
 * Candidate usernames for a person, best first.
 *
 * `taken` is what the caller already knows is unavailable — the database
 * decides for real, but filtering here means the admin is rarely shown a name
 * that then fails.
 *
 * Given "Maryan Cali" at "Shifo Pharmacy" this produces maryan, maryan.cali,
 * maryan.shifo, maryancali, maryan1 … — deriving the namespacing from the shop
 * rather than appending a number, which is what makes the result readable.
 */
export function suggestUsernames(
  personName: string,
  pharmacyName: string,
  taken: readonly string[] = [],
  limit = 5,
): string[] {
  const unavailable = new Set(taken.map(normalizeUsername));
  const parts = personName.split(/\s+/).map(slug).filter(Boolean);

  const first = parts[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]! : "";
  const shop = slug(pharmacyName.split(/\s+/)[0] ?? "");

  const candidates = [
    first,
    last ? `${first}.${last}` : "",
    shop ? `${first}.${shop}` : "",
    last ? `${first}${last}` : "",
    last ? `${first}.${last[0]!}` : "",
    ...Array.from({ length: 20 }, (_, i) => `${first}${i + 1}`),
  ];

  const out: string[] = [];
  for (const c of candidates) {
    if (!c || out.length >= limit) continue;
    const u = normalizeUsername(c).slice(0, 30);
    if (VALID.test(u) && !unavailable.has(u) && !out.includes(u)) out.push(u);
  }
  return out;
}
